/**
 * Centralized query key factory for TanStack Query.
 *
 * Every query key used in the app should be defined here so that:
 *  - Keys are type-safe and consistent across hooks.
 *  - Cache invalidation is predictable (invalidate a parent key to clear
 *    all children, e.g. `queryKeys.portfolios.all`).
 *  - No accidental key collisions between unrelated queries.
 */

import type {
  CapabilityMapParams,
  CategoryTreeParams,
  EcosystemParams,
  MatrixParams,
  MissionsParams,
  PartnershipsParams,
  ProductSearchParams,
  SuggestParams,
  SupplyFlowParams,
  TreemapParams,
  ValueChainParams,
} from "../../services/productPlaneApi";
import type { SearchFilters } from "../../services/searchApi";

export const queryKeys = {
  // ── Product plane ───────────────────────────────────────────────────
  productPlane: {
    all: ["product-plane"] as const,
    search: (params: ProductSearchParams) =>
      ["product-plane", "search", params] as const,
    suggest: (params: SuggestParams) =>
      ["product-plane", "suggest", params] as const,
    valueChain: (params: ValueChainParams) =>
      ["product-plane", "value-chain", params] as const,
    capabilityMap: (params: CapabilityMapParams) =>
      ["product-plane", "analytics", "capability-map", params] as const,
    ecosystem: (params: EcosystemParams) =>
      ["product-plane", "analytics", "ecosystem", params] as const,
    supplyFlow: (params: SupplyFlowParams) =>
      ["product-plane", "analytics", "supply-flow", params] as const,
    treemap: (params: TreemapParams) =>
      ["product-plane", "analytics", "treemap", params] as const,
    matrix: (params: MatrixParams) =>
      ["product-plane", "analytics", "matrix", params] as const,
    missions: (params: MissionsParams) =>
      ["product-plane", "analytics", "missions", params] as const,
    partnerships: (params: PartnershipsParams) =>
      ["product-plane", "analytics", "partnerships", params] as const,
    product: (productId: string, params?: Record<string, unknown>) =>
      ["product-plane", "product", productId, params] as const,
    category: (categoryId: string, params?: Record<string, unknown>) =>
      ["product-plane", "category", categoryId, params] as const,
    categoryTree: (params: CategoryTreeParams) =>
      ["product-plane", "category-tree", params] as const,
    capabilityPart: (partId: string, params?: Record<string, unknown>) =>
      ["product-plane", "capability-part", partId, params] as const,
    incident: (incidentId: string, params?: Record<string, unknown>) =>
      ["product-plane", "incident", incidentId, params] as const,
  },


  // ── Search ──────────────────────────────────────────────────────────
  search: {
    all: ["search"] as const,
    results: (entityType: string, filters: SearchFilters) =>
      ["search", entityType, filters] as const,
    autocomplete: (query: string) =>
      ["search", "autocomplete", query] as const,
  },

  // ── Graph / Map / Entity detail ─────────────────────────────────────
  graph: {
    all: ["graph"] as const,
    data: (entityId: string, options: Record<string, unknown>) =>
      ["graph", "data", entityId, options] as const,
    mapCoordinates: (filters: Record<string, unknown>) =>
      ["graph", "map", filters] as const,
  },

  entities: {
    all: ["entities"] as const,
    detail: (entityId: string) => ["entities", entityId] as const,
    edge: (relationshipId: string) =>
      ["entities", "edge", relationshipId] as const,
  },

  // ── Portfolios ──────────────────────────────────────────────────────
  portfolios: {
    all: ["portfolios"] as const,
    list: (params?: Record<string, unknown>) =>
      ["portfolios", "list", params] as const,
    detail: (id: string) => ["portfolios", id] as const,
    items: (id: string, filters?: Record<string, unknown>) =>
      ["portfolios", id, "items", filters] as const,
    summary: (id: string) => ["portfolios", id, "summary"] as const,
  },

  // ── Research ────────────────────────────────────────────────────────
  research: {
    all: ["research"] as const,
    projects: (params?: Record<string, unknown>) =>
      ["research", "projects", params] as const,
    project: (id: string) => ["research", "projects", id] as const,
    tracks: (params?: Record<string, unknown>) =>
      ["research", "tracks", params] as const,
    track: (id: string) => ["research", "tracks", id] as const,
    trackEntities: (trackId: string) =>
      ["research", "track-entities", trackId] as const,
    trackActivities: (trackId: string) =>
      ["research", "track-activities", trackId] as const,
    activeRuns: ["research", "active-runs"] as const,
  },

  // ── Saved Searches ──────────────────────────────────────────────────
  savedSearches: {
    all: ["saved-searches"] as const,
    list: (type?: string) => ["saved-searches", type] as const,
    detail: (id: string) => ["saved-searches", id] as const,
  },
} as const;
